import { gql } from "@apollo/client";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { strings } from "@resource/common";
import ConfirmationButton from "components/legacy/ConfirmationButton/ConfirmationButton";
import { useFlashMessage } from "components/legacy/FlashMessage/FlashMessage";
import {
  BulkUpdateStageTemplateInstallations,
  BulkUpdateStageTemplateInstallationsInput,
  BulkUpdateStageTemplateInstallationsVariables,
  JobRoleGuideTemplatesForBulkEdit_currentOrganization_jobRoleGuideTemplates as JobRoleGuideTemplate,
} from "generated/schemaTypes";
import _ from "lodash";
import React from "react";
import { ResourceCustomTheme } from "styles/config";
import useMutation from "utils/useMutation";

import ConfirmStageEdits from "./ConfirmStageEdits";
import {
  ActionEnum,
  BulkStageEditMode,
  StageEditsDispatchType,
  StageEditsStateType,
} from "./types";

const BULK_UPDATE_STAGE_TEMPLATE_INSTALLATIONS = gql`
  mutation BulkUpdateStageTemplateInstallations(
    $input: BulkUpdateStageTemplateInstallationsInput!
  ) {
    bulkUpdateStageTemplateInstallations(input: $input) {
      success
      message
      stageTemplateInstallations {
        id
        activatedAt
        displayName
        hidden
        contentTemplate {
          id
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {},
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  chip: {
    backgroundColor: theme.colors.RoyalPurple(100),
    color: theme.colors.RoyalPurple(),
    marginBottom: theme.spacing(2),
  },
}));

type ConfirmEditsProps = {
  mode: BulkStageEditMode;
  state: StageEditsStateType;
  dispatch: React.Dispatch<StageEditsDispatchType>;
  jobRoleGuideTemplates: JobRoleGuideTemplate[];
};

const ConfirmEdits: React.FC<ConfirmEditsProps> = ({
  mode,
  state,
  dispatch,
  jobRoleGuideTemplates,
}) => {
  const classes = useStyles();

  const { setContent } = useFlashMessage();

  const [bulkUpdateStageTemplateInstallations, { loading: saving }] =
    useMutation<
      BulkUpdateStageTemplateInstallations,
      BulkUpdateStageTemplateInstallationsVariables
    >(BULK_UPDATE_STAGE_TEMPLATE_INSTALLATIONS);

  const onSubmit = async () => {
    if (!state.stageIds.length) {
      console.error("Submitting without selected stages shouldn't be possible");
      return;
    }

    // eslint-disable-next-line no-underscore-dangle
    let _set: Omit<
      BulkUpdateStageTemplateInstallationsInput,
      "jobRoleGuideTemplateIds" | "atsStageIds"
    > = {};
    if (mode === BulkStageEditMode.Add) {
      _set = {
        ..._set,
        activatedAt: new Date(),
      };
    } else if (mode === BulkStageEditMode.Remove) {
      _set = {
        ..._set,
        activatedAt: null,
      };
    }
    if (state.contentTemplate) {
      _set = {
        ..._set,
        contentTemplateId: state.contentTemplate.id,
      };
    }
    if (state.emailTemplate) {
      _set = {
        ..._set,
        emailTemplateId: state.emailTemplate.id,
      };
    }
    if (state.emailActionMetadata) {
      _set = {
        ..._set,
        emailActionCC: _.join(state.emailActionMetadata.cc, ","),
        emailActionBCC: _.join(state.emailActionMetadata.bcc, ","),
      };
    }
    if (state.stageNameOverride) {
      _set = {
        ..._set,
        displayName: state.stageNameOverride.displayName,
      };
    }

    const res = await bulkUpdateStageTemplateInstallations({
      variables: {
        input: {
          jobRoleGuideTemplateIds: _.map(jobRoleGuideTemplates, "id"),
          atsStageIds: state.stageIds,
          ..._set,
        },
      },
    });

    const numUpdated =
      res.data?.bulkUpdateStageTemplateInstallations?.stageTemplateInstallations
        ?.length || 0;

    if (numUpdated > 0) {
      let confirmationText = "";
      switch (mode) {
        case BulkStageEditMode.Add:
          confirmationText = "added";
          break;
        case BulkStageEditMode.Edit:
          confirmationText = "applied your changes to";
          break;
        case BulkStageEditMode.Remove:
          confirmationText = "removed";
          break;
        default:
          break;
      }

      setContent({
        content: `🎉 Successfully ${confirmationText} ${strings.pluralize(
          "stage",
          numUpdated
        )}`,
        autoHideDuration: 5000,
        severity: "success",
      });
    } else {
      setContent({
        content:
          "It looks like we couldn't apply your changes. Check your filters and try again.",
        autoHideDuration: 5000,
        severity: "error",
      });
    }

    dispatch({
      type: ActionEnum.Finish,
    });
  };

  return (
    <>
      <DialogContent dividers>
        <ConfirmStageEdits mode={mode} data={state} />
      </DialogContent>

      <DialogActions>
        <Grid container>
          <Grid item xs={12} className={classes.actions}>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => {
                dispatch({ type: ActionEnum.Back });
              }}
              style={{ marginLeft: 10 }}
            >
              Back
            </Button>
            <ConfirmationButton
              disableTimer
              color="primary"
              disabled={saving}
              variant="contained"
              onClick={onSubmit}
              style={{ marginLeft: 10 }}
            >
              Confirm
            </ConfirmationButton>
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

export default ConfirmEdits;
